import { useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, Loader2 } from "lucide-react";
import { api, formatApiError, waLink } from "../lib/api";
import { useSettings } from "../lib/useSettings";
import { WhatsAppIcon } from "./FloatingWhatsApp";

const SLOTS = ["2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM"];

const inputCls =
  "w-full rounded-lg bg-slate-900/70 border border-white/10 px-4 py-2.5 text-sm text-slate-100 placeholder-slate-500 outline-none transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500";
const labelCls = "block text-xs font-medium text-slate-300 mb-1.5";

export default function DemoBooking() {
  const settings = useSettings();
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({ name: "", mobile: "", email: "", date: "", slot: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (ev) => {
    ev.preventDefault();
    if (submitting || result) return;
    if (!form.date) return toast.error("Please pick a date for your demo.");
    if (!form.slot) return toast.error("Please choose a time slot between 2:00 PM and 4:00 PM.");
    if (!form.consent) return toast.error("Consent is required to book a demo.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/demo-bookings", form);
      setResult(data);
      toast.success(data.status === "duplicate" ? "You already have a booking for this date." : "Demo booked successfully.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div data-testid="demo-booking-success" className="glass-card rounded-2xl p-8 sm:p-10 text-center">
        <CalendarCheck className="mx-auto text-green-400 mb-4" size={44} />
        <h3 className="font-display text-2xl font-bold text-slate-50">
          {result.status === "duplicate" ? "Booking already exists" : "Your demo is booked"}
        </h3>
        <p className="mt-3 text-sm text-slate-400">
          Reference <span className="font-mono2 text-blue-400" data-testid="demo-booking-reference">{result.reference}</span>
          {" "}&middot; {form.date.split("-").reverse().join("-")} at {form.slot} IST. Our team will confirm your slot shortly.
        </p>
        <a
          href={waLink(settings, `Hello RIDGELINE DIGI PARTNER, I booked a live demo (${result.reference}) for ${form.date} at ${form.slot}.`)}
          target="_blank" rel="noopener noreferrer"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-green-500 px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-105"
        >
          <WhatsAppIcon size={18} /> Confirm on WhatsApp
        </a>
      </div>
    );
  }

  return (
    <form data-testid="demo-booking-form" onSubmit={submit} className="glass-card rounded-2xl p-6 sm:p-8" noValidate>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="demo-name">Full Name *</label>
          <input id="demo-name" data-testid="demo-name-input" className={inputCls} value={form.name}
            onChange={(e) => set("name", e.target.value)} placeholder="Your full name" />
        </div>
        <div>
          <label className={labelCls} htmlFor="demo-mobile">Mobile Number *</label>
          <input id="demo-mobile" data-testid="demo-mobile-input" className={inputCls} value={form.mobile}
            onChange={(e) => set("mobile", e.target.value)} placeholder="+91 ..." />
        </div>
        <div>
          <label className={labelCls} htmlFor="demo-email">Business Email *</label>
          <input id="demo-email" type="email" data-testid="demo-email-input" className={inputCls} value={form.email}
            onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" />
        </div>
        <div>
          <label className={labelCls} htmlFor="demo-date">Preferred Date * <span className="text-slate-500">(any day)</span></label>
          <input id="demo-date" type="date" min={today} data-testid="demo-date-input"
            className={`${inputCls} [color-scheme:dark]`} value={form.date}
            onChange={(e) => set("date", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <span className={labelCls}>Time Slot * <span className="text-slate-500">(available 2:00 PM – 4:00 PM IST only)</span></span>
          <div className="flex flex-wrap gap-2 mt-1">
            {SLOTS.map((s) => (
              <button type="button" key={s} onClick={() => set("slot", s)}
                data-testid={`demo-slot-${s.replace(/[:\s]/g, "-").toLowerCase()}`}
                className={`rounded-full border px-5 py-2 text-xs font-semibold transition-all duration-200 ${
                  form.slot === s
                    ? "border-blue-500 bg-blue-600 text-white shadow-[0_0_18px_rgba(37,99,235,0.4)]"
                    : "border-white/10 bg-slate-900/60 text-slate-300 hover:border-blue-500/50"
                }`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" data-testid="demo-consent-checkbox" checked={form.consent}
              onChange={(e) => set("consent", e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-white/20 bg-slate-900 accent-blue-600" />
            <span className="text-xs text-slate-400 leading-relaxed">
              I consent to RIDGELINE DIGI PARTNER contacting me to confirm and conduct this demo session.
            </span>
          </label>
        </div>
      </div>
      <button type="submit" data-testid="demo-booking-submit-btn" disabled={submitting}
        className="mt-6 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-blue-600 px-8 py-3.5 text-sm font-semibold text-white shadow-[0_0_28px_rgba(37,99,235,0.4)] transition-all duration-300 hover:bg-blue-500 disabled:opacity-60">
        {submitting && <Loader2 className="animate-spin" size={16} />}
        {submitting ? "Booking..." : "Book My Live Demo"}
      </button>
    </form>
  );
}
